import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Send, Mail, RefreshCw } from "lucide-react";
import { API } from "@/App";
import SafeHtml from "../SafeHtml";

const TABS = [
  { id: "edit", label: "Edit" },
  { id: "preview", label: "Preview" },
];

const SendQuoteEmailDialog = ({ open, onOpenChange, quote, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [recipient, setRecipient] = useState("");
  const [variables, setVariables] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [tab, setTab] = useState("edit");
  const bodyRef = useRef(null);

  // Load defaults when dialog opens
  useEffect(() => {
    if (!open || !quote?.id) return;
    let cancelled = false;
    setLoading(true);
    setTab("edit");
    axios
      .get(`${API}/quotes/${quote.id}/email-preview`)
      .then((resp) => {
        if (cancelled) return;
        setSubject(resp.data.default_subject || "");
        setBody(resp.data.default_body || "");
        setButtonText(resp.data.default_button_text || "View Your Quote");
        setRecipient(resp.data.to || quote.customer_email || "");
        setVariables(resp.data.variables || []);
        setPreviewHtml(resp.data.html || "");
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load email preview");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, quote?.id, quote?.customer_email]);

  const refreshPreview = useCallback(async () => {
    if (!quote?.id) return;
    setPreviewLoading(true);
    try {
      const resp = await axios.post(`${API}/quotes/${quote.id}/email-preview`, {
        subject, body, button_text: buttonText, origin_url: window.location.origin,
      });
      setPreviewHtml(resp.data.html || "");
    } catch {
      toast.error("Could not render preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [quote?.id, subject, body, buttonText]);

  const handleTabChange = async (next) => {
    setTab(next);
    if (next === "preview") await refreshPreview();
  };

  const insertVariable = (key) => {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // restore caret after React updates
    requestAnimationFrame(() => {
      if (bodyRef.current) {
        const pos = start + token.length;
        bodyRef.current.focus();
        bodyRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleSend = async () => {
    if (!quote?.id) return;
    if (!recipient) { toast.error("Customer email is missing"); return; }
    if (!subject.trim()) { toast.error("Subject cannot be empty"); return; }
    if (!body.trim()) { toast.error("Email body cannot be empty"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/email`, {
        origin_url: window.location.origin,
        subject: subject.trim(),
        body: body.trim(),
        button_text: (buttonText || "").trim() || undefined,
      });
      toast.success("Quote email sent");
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send quote email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="send-quote-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#00341d]" />
            Send Quote {quote?.quote_number ? `#${quote.quote_number}` : ""}
          </DialogTitle>
          <DialogDescription>
            Fully customize the email. Use {`{{variable}}`} placeholders — they'll be replaced when sent.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <span className="w-6 h-6 border-2 border-[#00341d]/30 border-t-[#00341d] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="send-quote-to" className="text-xs uppercase tracking-wider text-zinc-500">To</Label>
                <Input
                  id="send-quote-to"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  disabled
                  className="bg-zinc-50 mt-1"
                  data-testid="send-quote-recipient"
                />
              </div>
              <div>
                <Label htmlFor="send-quote-button" className="text-xs uppercase tracking-wider text-zinc-500">Button Text</Label>
                <Input
                  id="send-quote-button"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="View Your Quote"
                  className="mt-1"
                  data-testid="send-quote-button-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="send-quote-subject" className="text-xs uppercase tracking-wider text-zinc-500">Subject</Label>
              <Input
                id="send-quote-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
                data-testid="send-quote-subject-input"
              />
            </div>

            <div className="border-b border-zinc-200 flex gap-1 mt-2">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleTabChange(t.id)}
                  className={`px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                    tab === t.id
                      ? "text-[#00341d] border-b-2 border-[#00341d]"
                      : "text-zinc-500 hover:text-[#00341d]"
                  }`}
                  data-testid={`send-quote-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
              {tab === "preview" && (
                <button
                  type="button"
                  onClick={refreshPreview}
                  className="ml-auto text-xs text-zinc-500 hover:text-[#00341d] flex items-center gap-1 px-2"
                  data-testid="send-quote-refresh-preview"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              )}
            </div>

            {tab === "edit" ? (
              <div>
                <Label htmlFor="send-quote-body" className="text-xs uppercase tracking-wider text-zinc-500">
                  Email Body
                </Label>
                <Textarea
                  id="send-quote-body"
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={14}
                  placeholder="Write your email..."
                  data-testid="send-quote-body-input"
                  className="resize-y font-mono text-sm mt-1"
                />
                <p className="text-xs text-zinc-400 mt-1">
                  Blank lines = paragraph breaks. The logo, button, and Land ClearCo footer are added automatically.
                </p>
                {variables.length > 0 && (
                  <div className="mt-3" data-testid="send-quote-variables">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5">
                      Insert variable
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {variables.map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => insertVariable(v.key)}
                          className="text-xs px-2 py-1 rounded-md border border-[#00341d]/30 bg-[#00341d]/5 text-[#00341d] hover:bg-[#00341d]/10 transition-colors"
                          data-testid={`send-quote-var-${v.key}`}
                          title={`Inserts {{${v.key}}}`}
                        >
                          {v.label}
                          <span className="text-zinc-400 ml-1">{`{{${v.key}}}`}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
                {previewLoading ? (
                  <div className="py-12 flex items-center justify-center">
                    <span className="w-5 h-5 border-2 border-[#00341d]/30 border-t-[#00341d] rounded-full animate-spin" />
                  </div>
                ) : (
                  <SafeHtml
                    className="max-h-[500px] overflow-y-auto"
                    data-testid="send-quote-preview"
                    html={previewHtml}
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-3 border-t border-zinc-200">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !recipient}
                className="bg-[#00341d] hover:bg-[#002816] text-white"
                data-testid="send-quote-confirm-btn"
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendQuoteEmailDialog;
